const React = require('react')
const { useState, useEffect } = require('react')
const { useParams, useHistory } = require('react-router-dom')
const { Button, CardHeader, CardBody, Collapse } = require('reactstrap')
const { useNetworkConfig } = require('@contexts')
const { CopyButton } = require('../../copyButton')
const { ZERO_ADDRESS } = require('@constants')

const { createPublicClient, http } = require('viem')

const ExpandableRead = props => {
    const { data, abi, account, index, func, isExpanded, onToggle } = props
    const history = useHistory()
    const { address } = useParams()
    const { network } = useNetworkConfig()

    const [inputValues, setInputValues] = useState({})
    const [queryResult, setQueryResult] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    const [inputErrors, setInputErrors] = useState({})

    useEffect(() => {
        if (isExpanded && func.inputs.length === 0) {
            handleQuery()
        }
    }, [isExpanded, func.inputs.length])

    const validateInputs = () => {
        const errors = {}
        let isValid = true

        func.inputs.forEach((input, i) => {
            if (input.type !== 'string' && !inputValues[i]) {
                errors[i] = 'This field is required'
                isValid = false
            }
        })

        setInputErrors(errors)
        return isValid
    }

    const handleHeaderClick = e => {
        const isCopyButtonClick = e.target.closest('[data-copy-button]') !== null
        if (!isCopyButtonClick) {
            onToggle()
        }
    }

    const handleInputChange = (inputIndex, value) => {
        setInputValues(prev => ({
            ...prev,
            [inputIndex]: value
        }))
    }

    const handleQuery = async () => {
        if (!validateInputs()) return
        setIsLoading(true)
        try {
            const args = func.inputs.map((_, i) => inputValues[i] || '')
            const viem = createPublicClient({
                transport: http(network.data.endpoint)
            })

            const response = await viem.readContract({
                address: address,
                abi: data.information.contract.abi,
                functionName: func.name,
                args: args,
                account: account ? account : ZERO_ADDRESS
            })

            const result = abi[index].outputs.map((output, i) => {
                return {
                    name: output.name,
                    type: output.type,
                    value: Array.isArray(response) ? response[i].toString() : response.toString()
                }
            })

            setQueryResult(result)
        } catch (error) {
            setQueryResult(`Error: ${error.message}`)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div style={{ marginBottom: '10px' }}>
            <CardHeader
                onClick={handleHeaderClick}
                style={{
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                    borderBottomLeftRadius: isExpanded ? '0px' : '4px',
                    borderBottomRightRadius: isExpanded ? '0px' : '4px',
                    cursor: 'pointer',
                    padding: '10px 15px',
                    backgroundColor: 'var(--dropdown-background)',
                    color: 'var(--white-color)'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <span className={`${network.name}-text-color-after`} style={{ fontWeight: 'normal' }}>
                        {index + 1}. {func.name} ({func.signature})
                    </span>
                    <span>
                        <span data-copy-button='true'>
                            <CopyButton
                                id={`copy-function-name-${func.name}`}
                                data={func.name}
                                text={'Copy Method Name'}
                            />
                        </span>
                        <i
                            className={`fas fa-arrow-${isExpanded ? 'down' : 'right'}`}
                            style={{ marginLeft: '8px', color: 'var(--grey-color)' }}
                        />
                    </span>
                </div>
            </CardHeader>
            <Collapse isOpen={isExpanded}>
                <CardBody
                    style={{
                        padding: '15px',
                        backgroundColor: '#1e1e1e',
                        borderBottomLeftRadius: '4px',
                        borderBottomRightRadius: '4px'
                    }}
                >
                    <div style={{ marginBottom: '10px' }}>
                        {func.inputs.map((input, i) => (
                            <div key={i} style={{ marginBottom: '12px' }}>
                                <label style={{ marginBottom: '5px', fontSize: '13px', color: 'var(--white-color)' }}>
                                    {input.name} ({input.type})
                                </label>
                                <input
                                    type='text'
                                    value={inputValues[i] || ''}
                                    onChange={e => handleInputChange(i, e.target.value)}
                                    placeholder={`${input.name} (${input.type})`}
                                    style={{
                                        width: '100%',
                                        padding: '8px 10px',
                                        background: 'var(--dropdown-background)',
                                        border: `1px solid ${inputErrors[i] ? 'red' : 'var(--grey-color)'}`,
                                        borderRadius: '4px',
                                        fontSize: '13px',
                                        color: 'var(--white-color)'
                                    }}
                                />
                                {inputErrors[i] && (
                                    <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
                                        {inputErrors[i]}
                                    </div>
                                )}
                            </div>
                        ))}
                        {func.inputs.length === 0 ? (
                            <div>
                                {queryResult?.map(item => (
                                    <p key={item.type + item.value} style={{ wordBreak: 'break-all' }}>
                                        {item.name && (
                                            <span style={{ fontWeight: 'bold', color: 'var(--grey-color)' }}>
                                                {item.name}&nbsp;
                                            </span>
                                        )}
                                        <span style={{ fontStyle: 'italic', color: 'var(--grey-color)' }}>
                                            {item.type}&nbsp;
                                        </span>
                                        {item.type === 'address' ? (
                                            <span
                                                className={`${network.name}-text-color cursor-pointer`}
                                                onClick={() => {
                                                    history.push(`/${network.name}/address/${item.value}`)
                                                }}
                                            >
                                                {item.value}
                                            </span>
                                        ) : (
                                            <>{item.value}</>
                                        )}
                                    </p>
                                ))}
                            </div>
                        ) : (
                            <>
                                <Button
                                    onClick={handleQuery}
                                    disabled={isLoading}
                                    style={{
                                        padding: '4px 12px',
                                        background: 'var(--dropdown-background)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        marginTop: '10px'
                                    }}
                                >
                                    {isLoading ? 'Querying...' : 'Query'}
                                </Button>
                                {queryResult && (
                                    <div
                                        style={{
                                            marginTop: '15px',
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {queryResult?.map(item => (
                                            <p key={item.type + item.value}>
                                                {item.name && (
                                                    <span style={{ fontWeight: 'bold', color: 'var(--grey-color)' }}>
                                                        {item.name}&nbsp;
                                                    </span>
                                                )}
                                                <span style={{ fontStyle: 'italic', color: 'var(--grey-color)' }}>
                                                    {item.type}&nbsp;
                                                </span>
                                                {item.type === 'address' ? (
                                                    <span
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() => {
                                                            history.push(`/${network.name}/address/${item.value}`)
                                                        }}
                                                    >
                                                        {item.value}
                                                    </span>
                                                ) : (
                                                    <>{item.value}</>
                                                )}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </CardBody>
            </Collapse>
        </div>
    )
}

module.exports = { ExpandableRead }
